import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import * as api from "../api/oncall.js";

export function useSchedules(options = {}) {
  return useQuery({ queryKey: keys.oncall.schedules, queryFn: api.listSchedules, ...options });
}

export function useSchedule(id, options = {}) {
  return useQuery({
    queryKey: keys.oncall.schedule(id),
    queryFn: () => api.getSchedule(id),
    enabled: !!id,
    ...options,
  });
}

export function useScheduleShifts(id, from, to, options = {}) {
  return useQuery({
    queryKey: ["oncall", "shifts", id, from, to],
    queryFn: () => api.getScheduleShifts(id, from, to),
    enabled: !!id,
    ...options,
  });
}

export function useCurrentOnCall(options = {}) {
  return useQuery({
    queryKey: ["oncall", "current"],
    queryFn: api.getCurrentOnCall,
    ...options,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createSchedule,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.oncall.all }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updateSchedule(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.oncall.all }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteSchedule,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.oncall.all }),
  });
}

export function useCreateRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, data }) => api.createRotation(scheduleId, data),
    onSuccess: (_d, { scheduleId }) => {
      qc.invalidateQueries({ queryKey: keys.oncall.schedule(scheduleId) });
      qc.invalidateQueries({ queryKey: keys.oncall.schedules });
    },
  });
}

export function useUpdateRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, rotationId, data }) =>
      api.updateRotation(scheduleId, rotationId, data),
    onSuccess: (_d, { scheduleId }) => {
      qc.invalidateQueries({ queryKey: keys.oncall.schedule(scheduleId) });
      qc.invalidateQueries({ queryKey: keys.oncall.schedules });
    },
  });
}

export function useDeleteRotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, rotationId }) =>
      api.deleteRotation(scheduleId, rotationId),
    onSuccess: (_d, { scheduleId }) => {
      qc.invalidateQueries({ queryKey: keys.oncall.schedule(scheduleId) });
      qc.invalidateQueries({ queryKey: keys.oncall.schedules });
    },
  });
}

export function useCreateOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, data }) => api.createOverride(scheduleId, data),
    onSuccess: (_d, { scheduleId }) =>
      qc.invalidateQueries({ queryKey: keys.oncall.schedule(scheduleId) }),
  });
}

export function useDeleteOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, overrideId }) =>
      api.deleteOverride(scheduleId, overrideId),
    onSuccess: (_d, { scheduleId }) =>
      qc.invalidateQueries({ queryKey: keys.oncall.schedule(scheduleId) }),
  });
}

export function useTakeover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.takeover,
    onSuccess: (_d, scheduleId) =>
      qc.invalidateQueries({ queryKey: keys.oncall.schedule(scheduleId) }),
  });
}

export function useEscalationPolicies(options = {}) {
  return useQuery({
    queryKey: keys.oncall.escalationPolicies,
    queryFn: api.listEscalationPolicies,
    ...options,
  });
}

export function useEscalationPolicy(id, options = {}) {
  return useQuery({
    queryKey: ["oncall", "escalation-policy", id],
    queryFn: () => api.getEscalationPolicy(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreateEscalationPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createEscalationPolicy,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.oncall.escalationPolicies }),
  });
}

export function useUpdateEscalationPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updateEscalationPolicy(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.oncall.escalationPolicies }),
  });
}

export function useDeleteEscalationPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteEscalationPolicy,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.oncall.escalationPolicies }),
  });
}
